import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fmtDateBoth } from '../utils/dates';

const API = process.env.REACT_APP_API || '/api';

const PAY_TYPES = [
  { value: 'none', label: 'غير محدَّد' },
  { value: 'salary', label: 'راتب ثابت' },
  { value: 'piece', label: 'بالقطعة' },
  { value: 'mixed', label: 'راتب + قطعة' },
];

function money(v, currency) {
  const n = Number(v || 0);
  return `${n.toLocaleString('ar-EG', { maximumFractionDigits: 2 })} ${currency}`;
}

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

export default function Payroll() {
  const { api, token } = useAuth();
  const toast = useToast();
  const [tab, setTab] = useState('monthly');
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const [items, setItems] = useState([]);
  const [itemForm, setItemForm] = useState({ name: '', unit: '', unit_price: '', requires_quantity: true });
  const [editingItem, setEditingItem] = useState(null);

  const [workers, setWorkers] = useState([]);
  const [rates, setRates] = useState([]);
  const [currency, setCurrency] = useState('ريال');

  const loadMonth = useCallback(() => {
    setLoading(true);
    api(`/payroll/monthly?month=${month}`)
      .then(d => { setData(d); setCurrency(d.currency); })
      .catch(err => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [month]);

  const loadCatalog = useCallback(() => {
    api('/pay-items?all=1').then(setItems).catch(() => {});
    api('/payroll/workers').then(setWorkers).catch(() => {});
    api('/payroll/rates').then(setRates).catch(() => {});
  }, []);

  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  const saveItem = async () => {
    if (!itemForm.name.trim()) return;
    const body = JSON.stringify({
      name: itemForm.name.trim(),
      unit: itemForm.unit.trim() || null,
      unit_price: Number(itemForm.unit_price || 0),
      requires_quantity: !!itemForm.requires_quantity,
    });
    try {
      if (editingItem) {
        await api(`/pay-items/${editingItem}`, { method: 'PUT', body });
        toast.success('تم تحديث البند');
      } else {
        await api('/pay-items', { method: 'POST', body });
        toast.success('تمت إضافة البند');
      }
      setItemForm({ name: '', unit: '', unit_price: '', requires_quantity: true });
      setEditingItem(null);
      loadCatalog();
    } catch (err) { toast.error(err.message); }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`حذف بند "${item.name}"؟ الكشوف السابقة تحتفظ بمبالغها.`)) return;
    try {
      await api(`/pay-items/${item.id}`, { method: 'DELETE' });
      loadCatalog();
    } catch (err) { toast.error(err.message); }
  };

  const saveWorker = async (worker, patch) => {
    try {
      await api(`/users/${worker.id}`, { method: 'PUT', body: JSON.stringify(patch) });
      setWorkers(ws => ws.map(w => (w.id === worker.id ? { ...w, ...patch } : w)));
      toast.success('تم الحفظ');
    } catch (err) { toast.error(err.message); }
  };

  const saveRate = async (userId, payItemId, value) => {
    try {
      await api('/payroll/rates', {
        method: 'PUT',
        body: JSON.stringify({
          user_id: userId,
          pay_item_id: payItemId,
          unit_price: value === '' ? null : Number(value),
        }),
      });
      api('/payroll/rates').then(setRates).catch(() => {});
    } catch (err) { toast.error(err.message); }
  };

  const settle = async (row) => {
    if (!window.confirm(`تسوية مستحقات ${row.user.name} عن ${data.label}؟ السطور المُسوّاة لا تتغيّر بعدها.`)) return;
    try {
      await api('/payroll/settle', { method: 'POST', body: JSON.stringify({ user_id: row.user.id, month }) });
      toast.success('تمت التسوية');
      loadMonth();
    } catch (err) { toast.error(err.message); }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/payroll/export?month=${month}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('فشل التصدير');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll-${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message); }
  };

  const rateFor = (userId, itemId) => {
    const r = rates.find(x => x.user_id === userId && x.pay_item_id === itemId);
    return r ? r.unit_price : '';
  };

  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>مستحقات الفريق</h2>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <button className={`btn btn-sm ${tab === 'monthly' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('monthly')}>الكشف الشهري</button>
        <button className={`btn btn-sm ${tab === 'items' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('items')}>بنود التسعير</button>
        <button className={`btn btn-sm ${tab === 'workers' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setTab('workers')}>أجور الأعضاء</button>
      </div>

      {tab === 'monthly' && (
        <>
          <div className="card" style={{ padding: 12, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">الشهر</label>
              <input type="month" className="form-input" value={month} onChange={e => setMonth(e.target.value)} />
            </div>
            <button className="btn btn-outline btn-sm" onClick={exportCsv}>📥 تصدير CSV</button>
          </div>

          {loading && <div className="card" style={{ padding: 20 }}>جارٍ التحميل…</div>}

          {!loading && data && (
            <>
              <div className="stats-grid" style={{ marginBottom: 16 }}>
                <div className="card" style={{ padding: 14 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>الرواتب الثابتة</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{money(data.totals.salaries, data.currency)}</div>
                </div>
                <div className="card" style={{ padding: 14 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>أعمال بالقطعة</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{money(data.totals.piece, data.currency)}</div>
                </div>
                <div className="card" style={{ padding: 14 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>الإجمالي</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{money(data.totals.grand, data.currency)}</div>
                </div>
                <div className="card" style={{ padding: 14 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>غير مُسوّى</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--c-warning)' }}>{money(data.totals.due, data.currency)}</div>
                </div>
              </div>

              {data.rows.length === 0 && (
                <div className="card" style={{ padding: 20, color: 'var(--text-muted)' }}>
                  لا مستحقات في هذا الشهر. حدِّد نوع أجر الأعضاء من تبويب «أجور الأعضاء» وسعِّر المهام ببنود التسعير.
                </div>
              )}

              {data.rows.map(row => (
                <div className="card" key={row.user.id} style={{ padding: 14, marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{row.user.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {row.pay_type_label} · {row.works_count} عمل
                        {row.unpriced_tasks > 0 && (
                          <span style={{ color: 'var(--c-warning)' }}> · {row.unpriced_tasks} مهمة منجزة بلا تسعير</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                      {row.salary > 0 && (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>الراتب</div>
                          <div>{money(row.salary, data.currency)}</div>
                        </div>
                      )}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>بالقطعة</div>
                        <div>{money(row.piece_total, data.currency)}</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>الإجمالي</div>
                        <div style={{ fontWeight: 700 }}>{money(row.total, data.currency)}</div>
                      </div>
                      <button className="btn btn-sm btn-outline" onClick={() => setExpanded(e => ({ ...e, [row.user.id]: !e[row.user.id] }))}>
                        {expanded[row.user.id] ? 'إخفاء الأعمال' : 'عرض الأعمال'}
                      </button>
                      {row.due_total > 0 && (
                        <button className="btn btn-sm btn-primary" onClick={() => settle(row)}>تسوية {money(row.due_total, data.currency)}</button>
                      )}
                    </div>
                  </div>

                  {expanded[row.user.id] && (
                    <div className="table-wrap" style={{ marginTop: 12 }}>
                      <table>
                        <thead>
                          <tr><th>العمل</th><th>الكمية</th><th>سعر الوحدة</th><th>المبلغ</th><th>التاريخ</th><th>الحالة</th></tr>
                        </thead>
                        <tbody>
                          {row.earnings.map(e => (
                            <tr key={e.id}>
                              <td>
                                {e.task_id
                                  ? <Link to={`/tasks/${e.task_id}`}>{e.description}</Link>
                                  : e.description}
                              </td>
                              <td>{e.quantity}{e.unit ? ` ${e.unit}` : ''}</td>
                              <td>{money(e.unit_price, data.currency)}</td>
                              <td style={{ fontWeight: 600 }}>{money(e.amount, data.currency)}</td>
                              <td>{fmtDateBoth(e.earned_on)}</td>
                              <td>
                                <span className={`badge badge-${e.status === 'paid' ? 'completed' : 'pending'}`}>
                                  {e.status === 'paid' ? 'مُسوّى' : 'مستحق'}
                                </span>
                              </td>
                            </tr>
                          ))}
                          {row.earnings.length === 0 && (
                            <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>لا أعمال مسعّرة في هذا الشهر</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </>
      )}

      {tab === 'items' && (
        <>
          <div className="card" style={{ padding: 16, marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 12 }}>{editingItem ? 'تعديل بند' : 'بند تسعير جديد'}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">اسم العمل</label>
                <input className="form-input" value={itemForm.name} placeholder="مثال: مونتاج"
                  onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">الوحدة</label>
                <input className="form-input" value={itemForm.unit} placeholder="ساعة / حلقة / تصميم"
                  onChange={e => setItemForm(f => ({ ...f, unit: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">سعر الوحدة ({currency})</label>
                <input className="form-input" type="number" min="0" step="0.01" value={itemForm.unit_price}
                  onChange={e => setItemForm(f => ({ ...f, unit_price: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, alignSelf: 'end' }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" checked={itemForm.requires_quantity}
                    onChange={e => setItemForm(f => ({ ...f, requires_quantity: e.target.checked }))} />
                  يطلب إدخال المقدار عند الإنجاز
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="btn btn-primary" onClick={saveItem} disabled={!itemForm.name.trim()}>
                {editingItem ? 'حفظ' : 'إضافة'}
              </button>
              {editingItem && (
                <button className="btn btn-outline" onClick={() => {
                  setEditingItem(null);
                  setItemForm({ name: '', unit: '', unit_price: '', requires_quantity: true });
                }}>إلغاء</button>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
              البند الذي «يطلب المقدار» لا تُقفل مهمته حتى يُدخل منفّذها مقدار عمله — مدة المحتوى مثلاً — فيُحتسب المبلغ = المقدار × سعر الوحدة.
            </div>
          </div>

          <div className="card" style={{ padding: 0 }}>
            <div className="table-wrap">
              <table>
                <thead><tr><th>البند</th><th>الوحدة</th><th>سعر الوحدة</th><th>المقدار</th><th>الحالة</th><th></th></tr></thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it.id}>
                      <td style={{ fontWeight: 500 }}>{it.name}</td>
                      <td>{it.unit || '—'}</td>
                      <td>{money(it.unit_price, currency)}</td>
                      <td>{it.requires_quantity ? 'يُدخَل عند الإنجاز' : 'ثابت (١)'}</td>
                      <td>
                        <span className={`badge badge-${it.is_active ? 'completed' : 'cancelled'}`}>
                          {it.is_active ? 'مفعّل' : 'موقوف'}
                        </span>
                      </td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-outline" onClick={() => {
                          setEditingItem(it.id);
                          setItemForm({
                            name: it.name, unit: it.unit || '',
                            unit_price: it.unit_price, requires_quantity: !!it.requires_quantity,
                          });
                        }}>تعديل</button>
                        <button className="btn btn-sm btn-outline" onClick={() => removeItem(it)}>حذف</button>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>لا بنود بعد</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {tab === 'workers' && (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>العضو</th><th>نوع الأجر</th><th>الراتب الشهري</th>
                  {items.filter(i => i.is_active).map(i => <th key={i.id}>{i.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {workers.map(w => (
                  <tr key={w.id}>
                    <td style={{ fontWeight: 500 }}>{w.name}</td>
                    <td>
                      <select className="form-input" style={{ minWidth: 130 }} value={w.pay_type || 'none'}
                        onChange={e => saveWorker(w, { pay_type: e.target.value })}>
                        {PAY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <input className="form-input" type="number" min="0" step="0.01" style={{ width: 110 }}
                        defaultValue={w.monthly_salary}
                        disabled={!['salary', 'mixed'].includes(w.pay_type)}
                        onBlur={e => {
                          if (Number(e.target.value) !== Number(w.monthly_salary)) {
                            saveWorker(w, { monthly_salary: Number(e.target.value || 0) });
                          }
                        }} />
                    </td>
                    {items.filter(i => i.is_active).map(i => (
                      <td key={i.id}>
                        <input className="form-input" type="number" min="0" step="0.01" style={{ width: 90 }}
                          placeholder={i.unit_price}
                          defaultValue={rateFor(w.id, i.id)}
                          onBlur={e => saveRate(w.id, i.id, e.target.value)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: 12, fontSize: 12, color: 'var(--text-muted)' }}>
            الخلية الفارغة تعني أن العضو يأخذ سعر الكتالوج. الأسعار الخاصة تُطبَّق على الأعمال الجديدة فقط — ما احتُسب سابقاً لا يتغيّر.
          </div>
        </div>
      )}
    </div>
  );
}
